import angular from 'angular';
import csmCommonWeb from 'csm-common-web';
import ledgerItemOption from '../../data/ledgerItemOption/ledgerItemOption';
import productItemOptionsListTemplate from './productItemOptionsList.template.html'

export default angular
  .module('books.ui.productItemOptionList', [csmCommonWeb, ledgerItemOption])
  .component('productItemOptionList', {
    require: { ngModel: 'ngModel' },
    bindings: {
      masterList: '<',
      product: '<',
      inventoryProducts: '<',
      forReport: '&',
      rowClass: '@',
      optionLabelClass: '@',
      inputDivClass: '@'
    },
    controller: ProductItemOptionListController,
    template: productItemOptionsListTemplate
  })
  .name;

/*****************************************************************************
 * Controller: productItemOptionList
 *
 * Renders option-input rows for a product (and any sub-items) against the
 * provided master option list. Selection-list options switch rendering based
 * on EACH product's own trackInventory flag — root and sub-items decide
 * independently. The tree can mix freely:
 *
 *   - Root tracked, sub-items untracked
 *   - Root untracked, sub-items tracked
 *   - Mix of tracked and untracked sub-items at any depth
 *
 * Each tracked product gets its own inventory section (heading + search +
 * combination grid). Untracked products keep the dropdown rendering for
 * their selection-list masters. Text-input masters render the same way for
 * both.
 *
 * All sections write to the same ngModel — an array of LedgerItemOption
 * records. Inventory toggle and selection-detection scope themselves to a
 * given productID so a sub-item's combination toggle never disturbs the
 * root's selections (or vice versa).
 *****************************************************************************/
/* @ngInject */
function ProductItemOptionListController(
  LedgerItemOption,
  $filter,
  $injector
) {
  const $ctrl = this;
  const $svcs = { $ctrl };

  $ctrl.$onInit = function () {
    /**
    *Hand initCtrl every dependency it needs to wire up factories. $svcs
    *gets a reference to itself so initCtrl can pass the same locals object
    *into each $injector.invoke call.
    */
    $injector.invoke(initCtrl, null, Object.assign($svcs, {
      $svcs,
      LedgerItemOption,
      $filter
    }));

    /**
    *$onChanges is defined here, inside $onInit, rather than as a top-level
    *method on the controller. Two reasons:
    *
    *  1. Co-location. The hook depends on methods that initCtrl just
    *     attached to $ctrl ($ctrl.regenerate, $ctrl.clearReusedModel).
    *     Defining the hook in this scope makes those references safe by
    *     construction — no need for an existence guard.
    *
    *The trade-off worth knowing about: AngularJS fires the *initial*
    *$onChanges before $onInit runs. At that moment our hook isn't defined
    *yet, so the first set of bindings does NOT reach this code path. We
    *compensate by calling $ctrl.regenerate() manually at the end of
    *$onInit — that's what produces the initial render. Subsequent binding
    *changes (parent reassigning masterList, product, inventoryProducts)
    *fire through this hook normally.
    *
    *If you ever switch this hook to a top-level controller method, drop
    *the manual $ctrl.regenerate() call below — AngularJS will start
    *delivering the initial change set on its own.
    */
    $ctrl.$onChanges = function (changes) {
      /**
      *When the bound product reference changes after the first render,
      *empty the model array if the parent kept the same array reference.
      *This prevents the previous product's selections from bleeding into
      *the new product. isFirstChange() is the guard so we don't clobber
      *the parent's seeded model on initial mount.
      */
      if (changes.product && !changes.product.isFirstChange()) {
        $ctrl.clearReusedModel();
      }

      /**
      *Only the three "shape" inputs trigger a rebuild. @ bindings
      *(rowClass, optionLabelClass, inputDivClass) are read by the template
      *on every digest already; rebuilding for them would be wasted work.
      */
      if (changes.masterList || changes.product || changes.inventoryProducts) {
        $ctrl.regenerate();
      }
    };

    /*-------------------------------------------------------------*/
    /* ngModel doesn't fire $onChanges — its updates flow through  */
    /* the $render hook on the ngModelController. Without this,    */
    /* the parent's seeded model arrives AFTER $onInit completes   */
    /* and regenerate never sees it: the initial regenerate() call */
    /* below reads $viewValue while it's still empty, builds an    */
    /* empty selection state, and the buttons render unselected    */
    /* even though the LIOs land in the model moments later.       */
    /*-------------------------------------------------------------*/
    $ctrl.ngModel.$render = function () {
      $ctrl.regenerate();
    };

    /**
    *Initial render. See the long comment above $onChanges for why this is
    *necessary rather than redundant.
    */
   $ctrl.regenerate();
  };
}

/*****************************************************************************
 * Method factories — alphabetical
 *
 * Each factory takes the dependencies it needs and returns the actual
 * function that gets attached to $ctrl. Cross-method calls go through $ctrl
 * so factories never need to import each other directly.
 *****************************************************************************/

/* @ngInject */
function buildInventoryOptions($ctrl, SERVER) {
  /**
   * Produces the flat list of combination button entries for ONE product —
   * called once per inventory-tracked product in the tree. Walks that
   * product's own itemOptions, Cartesian-products the selection-list
   * masters together, and matches each combination against the shared
   * inventoryProducts pool to pull the inventory count.
   *
   * Matching by itemOptionID inside inventoryProductsEqual makes the shared
   * pool naturally correct: sub-item combos only match inventoryProducts
   * whose options reference that sub-item's itemOptionIDs, root combos
   * only match the root's, and so on. No pre-filtering of the pool needed.
   *
   * Each returned entry contains:
   *   - key:            stable id used for ng-repeat track-by
   *   - name:           "Small - Black - Cotton" style label
   *   - inventoryCount: pulled from the matching InventoryProduct, or 0
   *   - options:        the underlying combo records, used by toggle and
   *                     isInventorySelected to compute model writes/reads
   *   - inventoryProduct: the matched InventoryProduct (for callers that
   *                       want extra info beyond just the count)
   */
  return function (masterList, product, inventoryProducts) {
    if (!masterList || !product || !inventoryProducts) return [];

    // Lookup so we can resolve master metadata while iterating itemOptions.
    const masterById = new Map(masterList.map((m) => [m.id, m]));

    /**
    *Group THIS product's active selection-list itemOptions by master id.
    *Each group becomes one axis of the Cartesian product below.
    */
    const groups = new Map();

    for (const io of product.itemOptions || []) {
      const master = masterById.get(io.optionID);

      /**
      *Skip non-selection-list options (those become text inputs elsewhere)
      *and skip inactive options entirely — inventory mode doesn't have a
      *notion of "show but don't allow selection" the way report mode does.
      */
      if (!master || !io.active || master.type !== SERVER.BooksDBConstants.MOT_SELECTION_LIST) continue;

      if (!groups.has(master.id)) groups.set(master.id, []);

      /**
      *Build a normalized option shape that downstream code (toggle,
      *isInventorySelected, toLedgerItemOption) consumes uniformly.
      *priceChange and qtyChange flow through so the parent's price math
      *works on the combo's records.
      */
      groups.get(master.id).push({
        itemOptionID: io.id,
        itemOptionValue: io.value,
        masterOptionID: master.id,
        masterOptionName: master.name,
        priceChange: io.priceChange || 0,
        qtyChange: io.qtyChange || 0
      });
    }

    /**
    *Cartesian product across the groups. Starting from a single empty
    *combo, expand it once per group: every existing partial combo
    *duplicates and each duplicate gets one option from the new group
    *appended. Final length = product of group sizes.
    */
    const combos = [...groups.values()].reduce(
      (acc, group) => acc.flatMap((combo) => group.map((opt) => [...combo, opt])),
      [[]]
    );

    /**
    *Drop the seeded empty combo (only present when no groups existed) and
    *shape each remaining combo into a button entry.
    */
    return combos
      .filter((combo) => combo.length > 0)
      .map((combo) => {
        const inventoryProduct = $ctrl.findMatchingInventoryProduct(combo, inventoryProducts);

        return {
          key: $ctrl.comboKey(combo),
          name: combo.map((o) => o.itemOptionValue).join(' - '),
          inventoryCount: inventoryProduct ? (inventoryProduct.count || 0) : 0,
          options: combo,
          inventoryProduct
        };
      });
  };
}

/* @ngInject */
function buildProductMap() {
  /**
   * Walks the root product (and all sub-items) and produces a lookup keyed
   * by productID:
   *
   *   {
   *     [productID]: {
   *       product,
   *       optionMap: {
   *         [masterOptionID]: { master, itemOptions }
   *       }
   *     }
   *   }
   *
   * Two non-obvious behaviors live here:
   *
   *   1. Inactive-option filtering. In edit mode (not report mode), we skip
   *      itemOptions whose `active` flag is false, AND itemOptions whose
   *      master is itself inactive. Report mode keeps everything because
   *      the multi-select needs to display historical data, not just
   *      currently-selectable choices.
   *
   *   2. Orphan preservation. If the receipt being edited references an
   *      itemOption that has since been deleted from the product, we
   *      synthesize a stand-in itemOption from the LedgerItemOption record
   *      and feed it into the same processItemOption path. This is what
   *      keeps "this receipt was rung up with Color: Maroon, but Maroon was
   *      deleted last week" still showing as a row in edit UI.
   */
  return function ({
    rootProduct,
    isReport,
    masterOptionLookup,
    validOptionIDs,
    modelValue
  }) {
    const productMap = {};

    function visit(product) {
      /**
      *Inner helper: ensure productMap has a slot for this product and the
      *master in question, then push the itemOption into it. Closures over
      *productMap and masterOptionLookup keep call sites concise.
      */
      const processItemOption = (io) => {
        if (!validOptionIDs.has(io.optionID)) return;

        let entry = productMap[product.id];
        if (!entry) entry = productMap[product.id] = { product, optionMap: {} };

        let bucket = entry.optionMap[io.optionID];
        if (!bucket) {
          bucket = entry.optionMap[io.optionID] = {
            master: masterOptionLookup[io.optionID],
            itemOptions: []
          };
        }

        bucket.itemOptions.push(io);
      };

      // First pass: the product's own currently-defined itemOptions.
      for (const io of product.itemOptions || []) {
        // Edit-mode active-flag filter. Report mode bypasses this.
        if (!isReport && (!io.active || !masterOptionLookup[io.optionID]?.active)) {
          continue;
        }

        processItemOption(io);
      }

      /**
      *Second pass: orphan preservation. Look at the model for entries that
      *belong to this product but reference itemOptions that are no longer
      *present on the product. Synthesize an itemOption for each one and
      *run it through processItemOption, so the row still renders.
      */
      modelValue
        .filter((lio) => lio.itemID === product.id)
        .filter((lio) => {
          const entry = productMap[product.id];
          const bucket = entry?.optionMap[lio.optionMasterID];

          /**
          *Keep this lio if it has no matching itemOption in the bucket
          *(or the bucket itself doesn't exist yet).
          */
          return (
            !entry ||
            !bucket ||
            !bucket.itemOptions.some((io) =>
              io.id === lio.itemOptionID
            )
          );
        })
        .forEach((lio) => {
          processItemOption({
            id: lio.itemOptionID,
            itemID: lio.itemID,
            optionID: lio.optionMasterID,
            optionName: lio.optionMasterName,
            priceChange: lio.priceChange,
            qtyChange: lio.qtyChange,
            value: lio.optionValue
          });
        });

      // Recurse into sub-items so they render their own option rows.
      (product.subItems || []).forEach(visit);
    }

    visit(rootProduct);
    return productMap;
  };
}

/* @ngInject */
function buildRow($ctrl, LedgerItemOption, SERVER) {
  /**
   * Produces a single row's view-model. The returned object's `type` field
   * tells the template which input element CAN render. Three types exist:
   *
   *   'multi'     report mode only — multi-select for picking which options
   *               to include in the report
   *   'text'      edit mode, non-selection-list masters — free-form input
   *               whose value becomes the LedgerItemOption's optionValue
   *   'dropdown'  edit mode, selection-list masters — single-select pick.
   *               When the row's product tracks inventory, hideDropdown is
   *               true and the template suppresses ONLY the <select>
   *               element. The row itself (label, required marker, price-
   *               change annotation, etc.) still renders. The actual
   *               selection mechanism for tracked products lives in the
   *               inventory-button grid further down the section.
   *
   * trackInventory is checked on the row's own product, so a sub-item that
   * tracks inventory hides its own dropdowns even when the root doesn't,
   * and vice versa.
   */
  return function (mm, product, isReport) {
    const masterID = mm.master.id;
    const currentValue = $ctrl.findCurrentValue(product.id, mm.master.id);

    /**
    *Fields shared across all three row types. Per-type fields get merged
    *in via Object.assign in each branch below.
    */
    const base = {
      masterID,
      master: mm.master,
      itemOptions: mm.itemOptions,
      productID: product.id
    };

    /*-------- REPORT MODE: multi-select over LedgerItemOption instances --*/
    if (isReport) {
      /**
      *Convert each ItemOption to a LedgerItemOption so the multi-select
      *displays consistent shapes that can be written to ngModel as-is.
      */
      const options = mm.itemOptions.map((io) => new LedgerItemOption(io));

      return Object.assign(base, {
        type: 'multi',
        options,
        // Default to all-selected, matching the original directive.
        selections: [...options],
        showPriceChange: false,
        hideDropdown: false
      });
    }

    /*-------- EDIT MODE, NON-SELECTION-LIST: text input -----------------*/
    if (mm.master.type !== SERVER.BooksDBConstants.MOT_SELECTION_LIST) {
      return Object.assign(base, {
        type: 'text',
        value: currentValue?.optionValue ?? '',
        master: mm.master,
        seedOption: mm.itemOptions[0],
        showPriceChange: (mm.itemOptions[0].priceChange || 0) > 0,
        priceChange: mm.itemOptions[0].priceChange || 0,
        hideDropdown: false
      });
    }

    /*-------- EDIT MODE, SELECTION-LIST -------------------------------*/
    if (product.trackInventory) {
      return Object.assign(base, {
        type: 'dropdown',
        hideDropdown: true,
        showPriceChange: false
      });
    }
    
    const itemOptions = mm.master.required
      ? mm.itemOptions.slice()
      : [{ id: null, value: '' }, ...mm.itemOptions];
    
    return Object.assign(base, {
      type: 'dropdown',
      hideDropdown: false,
      itemOptions,
      selectedID: currentValue?.itemOptionID ?? null,   // <-- just an id
      showPriceChange: false
    });
  };
}

/* @ngInject */
function buildSection($ctrl) {
  /**
   * Wraps a single product-map entry into a render-ready section. A
   * "section" represents one product (root or sub-item) and carries:
   *
   *   - optionRows                its text-input + dropdown + multi-select
   *                               rows (built via buildRow). For tracked
   *                               products, the selection-list dropdowns
   *                               are filtered out — buildRow returns null
   *                               and .filter(Boolean) drops them.
   *
   *   - inventoryOptions          the combination buttons for this product,
   *                               populated only when product.trackInventory
   *                               is true. Built per-product so each
   *                               tracked item gets its own grid.
   *
   *   - showInventory             gate for the inventory block in the
   *                               template — true only when there's
   *                               actually something to show.
   *
   *   - optionFuzzyInput          per-section search input state. Lives on
   *     filteredInventoryOptions  the section so each tracked product gets
   *                               its own independent search field.
   *
   *   - showLabel                 true when this is a sub-item AND there's
   *                               ambiguity to disambiguate (multiple
   *                               products in the tree). Single-product
   *                               trees skip the label entirely.
   */
  return function (pm, showSubLabels) {
    const isMain = pm.product.id === $ctrl.product.id;
    const tracksInventory = !!pm.product.trackInventory;

    /**
    *Build inventory options for THIS product if it tracks inventory.
    *Sub-items and root each get their own independent grids; the shared
    *inventoryProducts pool is filtered naturally by itemOptionID matching
    *inside findMatchingInventoryProduct.
    */
    const inventoryOptions = tracksInventory
      ? $ctrl.buildInventoryOptions(
        $ctrl.masterList,
        pm.product,
        $ctrl.inventoryProducts || []
      )
      : [];

    return {
      productID: pm.product.id,
      product: pm.product,
      isMain,
      showLabel: !isMain && showSubLabels,
      tracksInventory,
      inventoryOptions,
      showInventory: tracksInventory && inventoryOptions.length > 0,
      optionFuzzyInput: null,
      filteredInventoryOptions: null,
      optionRows: Object.values(pm.optionMap).map(
        (mm) => $ctrl.buildRow(mm, pm.product, $ctrl.isReport)
      )
    };
  };
}

/* @ngInject */
function clearOptionFuzzySearch() {
  /** Resets a section's inventory-options search input and filter result. */
  return function (section) {
    section.filteredInventoryOptions = null;
    section.optionFuzzyInput = null;
  };
}

/* @ngInject */
function clearReusedModel($ctrl) {
  /**
   * Empties the model array IN PLACE when the bound product changes but the
   * parent kept the same model array reference. This stops selections from
   * the previous product carrying over when the parent rebinds product
   * without also assigning a fresh model.
   *
   * If the parent did assign a new model array (so the reference differs),
   * we leave it alone — the new array is presumed to be the intended state
   * for the new product.
   */
  return function () {
    if (
      $ctrl.lastModelReference &&
      $ctrl.lastModelReference === $ctrl.ngModel.$modelValue
    ) {
      $ctrl.lastModelReference.splice(0, $ctrl.lastModelReference.length);
    }

    $ctrl.lastModelReference = $ctrl.ngModel.$modelValue;
  };
}

/*****************************************************************************
 * Selection-list master ID collection
 *
 * Walks the product tree (root + sub-items, recursively) and collects every
 * master ID referenced by a selection-list itemOption anywhere in the tree.
 *
 * This Set is the gate that isInventorySelected and toggleInventory use to
 * identify which model entries belong to the "inventory button" slice of
 * the model. The bound masterList alone isn't sufficient because the parent
 * may pass only the root's masters; sub-item masters would then fail the
 * gate and their previously-selected combos would render as unselected on
 * edit, even though the LedgerItemOption records are present in ngModel.
 *****************************************************************************/
/* @ngInject */ function collectSelectionListMasterIDs(SERVER, $ctrl) {
  return function (product, masterLookup, acc) {

    for (const io of product.itemOptions || []) {
      const master = masterLookup[io.optionID];
      
      if (master?.type === SERVER.BooksDBConstants.MOT_SELECTION_LIST) {
        acc.add(master.id);
      }
    }

     (product.subItems || []).forEach((sub) =>
       $ctrl.collectSelectionListMasterIDs(sub, masterLookup, acc)
     );

    return acc;
  }
}

/* @ngInject */
function comboKey() {
  /**
   * Stable string id for a combination, used as ng-repeat's track-by.
   * Sorting before joining guarantees that the same set of options always
   * produces the same key regardless of the order they were emitted.
   */
  return function (options) {
    return options
      .map((o) => `${o.masterOptionID}:${o.itemOptionID}`)
      .sort()
      .join('|');
  };
}

/* @ngInject */
function findCurrentValue($ctrl) {
  /**
   * Returns the LedgerItemOption already in the model for this
   * product+master, or undefined. Used by buildRow to seed initial state.
   */
  return function (productID, masterID) {
    return ($ctrl.ngModel.$modelValue || []).find(
      (lio) =>
        lio.itemID === productID &&
        lio.optionMasterID === masterID
    );
  };
}

/* @ngInject */
function findMatchingInventoryProduct($ctrl) {
  /**
   * Locates the InventoryProduct whose options match the given combination.
   * Wraps the combination in a probe object with the same shape that
   * inventoryProductsEqual expects on its second argument.
   *
   * Note: the inventoryProducts pool is shared across all tracked products
   * in the tree. The matching by itemOptionID inside inventoryProductsEqual
   * makes that shared pool naturally correct — sub-item combos only match
   * inventoryProducts whose options reference the sub-item's itemOptionIDs,
   * never the root's (and vice versa).
   */
  return function (combo, inventoryProducts) {
    const probe = { options: combo };

    return inventoryProducts.find((ip) =>
      $ctrl.inventoryProductsEqual(ip, probe)
    ) || null;
  };
}

/* @ngInject */
function formatOption($filter) {
  /** ng-options label function for the dropdown input. */
  return function (option) {
    return option.value +
      (option.priceChange ? ` (+${$filter('currency')(option.priceChange)})` : '');
  };
}

/* @ngInject */
function getLabelClasses($ctrl) {
  return function (master) {
    return [$ctrl.optionLabelClass, { req: master.required }];
  };
}

/* @ngInject */
function getVisibleInventoryOptions() {
  /**
   * Returns either the section's search-filtered list or its full list.
   * Centralizing this keeps the template binding to a single accessor —
   * the template never needs to know whether a filter is active.
   */
  return function (section) {
    return section.filteredInventoryOptions != null
      ? section.filteredInventoryOptions
      : section.inventoryOptions;
  };
}

/* @ngInject */
function initCtrl($ctrl, $injector, $svcs) {
  /**
   * Attaches every property and method the controller exposes. Called
   * once from $onInit. Each method uses $injector.invoke against $svcs so
   * the factory pattern (function name(deps) { return function (...) {} })
   * gets the deps it asked for. Cross-method calls go through $ctrl, so
   * factories never need to import each other directly.
   */
  Object.assign($ctrl, {
    /*------------*/
    /* Properties */
    /*------------*/
    productSections: [],
    selectionListMasterIDs: new Set(),
    lastModelReference: null,
    isReport: false,

    /*-----------*/
    /* Functions */
    /*-----------*/
    buildInventoryOptions: $injector.invoke(buildInventoryOptions, null, $svcs),
    buildProductMap: $injector.invoke(buildProductMap, null, $svcs),
    buildRow: $injector.invoke(buildRow, null, $svcs),
    buildSection: $injector.invoke(buildSection, null, $svcs),
    clearOptionFuzzySearch: $injector.invoke(clearOptionFuzzySearch, null, $svcs),
    clearReusedModel: $injector.invoke(clearReusedModel, null, $svcs),
    collectSelectionListMasterIDs: $injector.invoke(collectSelectionListMasterIDs, null, $svcs),
    comboKey: $injector.invoke(comboKey, null, $svcs),
    findCurrentValue: $injector.invoke(findCurrentValue, null, $svcs),
    findMatchingInventoryProduct: $injector.invoke(findMatchingInventoryProduct, null, $svcs),
    formatOption: $injector.invoke(formatOption, null, $svcs),
    getLabelClasses: $injector.invoke(getLabelClasses, null, $svcs),
    getVisibleInventoryOptions: $injector.invoke(getVisibleInventoryOptions, null, $svcs),
    inventoryProductsEqual: $injector.invoke(inventoryProductsEqual, null, $svcs),
    isInventorySelected: $injector.invoke(isInventorySelected, null, $svcs),
    onDropdownChange: $injector.invoke(onDropdownChange, null, $svcs),
    onMultiChange: $injector.invoke(onMultiChange, null, $svcs),
    onTextChange: $injector.invoke(onTextChange, null, $svcs),
    optionFuzzySearch: $injector.invoke(optionFuzzySearch, null, $svcs),
    regenerate: $injector.invoke(regenerate, null, $svcs),
    toLedgerItemOption: $injector.invoke(toLedgerItemOption, null, $svcs),
    toggleInventory: $injector.invoke(toggleInventory, null, $svcs),
    updateModel: $injector.invoke(updateModel, null, $svcs)
  });
}

/* @ngInject */
function inventoryProductsEqual() {
  /**
   * Mirrors InventoryProduct.equals: option counts must match, every option
   * in `a` must have a matching option in `b` by masterOptionID +
   * itemOptionID, with an id fallback for the default-product case
   * (no options on either).
   */
  return function (a, b) {
    if (!a || !b) return false;

    const aOptions = a.options || [];
    const bOptions = b.options || [];

    if (aOptions.length !== bOptions.length) return false;

    const allFound = aOptions.every((ao) =>
      bOptions.some(
        (bo) =>
          ao.masterOptionID === bo.masterOptionID && ao.itemOptionID === bo.itemOptionID
      )
    );

    return allFound || a.id === b.id;
  };
}

/* @ngInject */
function isInventorySelected($ctrl) {
  /**
   * True when the given combination is the one currently assigned to the
   * specified product's selection-list options in ngModel. Drives the
   * highlight (btn-info class) on each inventory button.
   *
   * The productID is passed in from the template (section.productID) so
   * each section's buttons check against their own product's slice of the
   * model. A sub-item highlighting one combination doesn't accidentally
   * highlight buttons in the root's grid (or vice versa).
   *
   * Only the selection-list slice of the model is considered — text-input
   * entries on the same product (different master types) live in the same
   * model array but should not affect inventory-button selection state.
   * That's what selectionListMasterIDs is for.
   */
  return function (option, productID) {
    if (!productID) return false;

    const ourEntries = ($ctrl.ngModel.$viewValue || []).filter(
      (m) =>
        m.itemID === productID &&
        $ctrl.selectionListMasterIDs.has(m.optionMasterID)
    );

    /**
    *Length mismatch is enough to short-circuit. Catches both
    *"model has fewer entries than this combo's options" and "model has
    *more entries than this combo's options" — only an exact set match
    *counts as selected.
    */
    if (ourEntries.length !== option.options.length) return false;

    return option.options.every((opt) =>
      ourEntries.some((m) => m.itemOptionID === opt.itemOptionID)
    );
  };
}

/* @ngInject */
function onDropdownChange($ctrl, LedgerItemOption) {
  return function (row) {
    /*------------------------------------------------------------*/
    /* Strip any existing entry for this row's (productID,        */
    /* masterID), then append the new selection if one exists.    */
    /* Single $setViewValue write — mirrors toggleInventory.      */
    /*------------------------------------------------------------*/
    const current = $ctrl.ngModel.$viewValue || [];
    const others = current.filter(
      (m) => !(m.itemID === row.productID && m.optionMasterID === row.master.id)
    );

    let next = others;
    if (row.selectedID != null) {
      const io = row.itemOptions.find((o) => o.id === row.selectedID);
      if (io) next = [...others, new LedgerItemOption({ ...io, itemID: row.productID })];
    }

    $ctrl.ngModel.$setViewValue(next);
  };
}

/* @ngInject */
function onMultiChange($ctrl) {
  /**
   * Multi-select selection changed. Diff against the previous selection
   * snapshot, remove anything that's gone, then re-add (idempotently)
   * everything still selected. The re-add ensures that if updateModel
   * somehow lost an entry between renders, the next change restores it.
   */
  return function (row) {
    const prev = row.lastSelections || [];
    const current = row.selections || [];

    prev.forEach((lio) => {
      if (!current.includes(lio)) $ctrl.updateModel(lio, true);
    });

    current.forEach((lio) => $ctrl.updateModel(lio, false));

    row.lastSelections = [...current];
  };
}

/* @ngInject */
function onTextChange($ctrl, LedgerItemOption) {
  return function (row) {
    const current = $ctrl.ngModel.$viewValue || [];
    const others = current.filter(
      (m) => !(m.itemID === row.productID && m.optionMasterID === row.master.id)
    );

    const next = row.value
      ? [...others, new LedgerItemOption({
          ...row.seedOption,
          itemID: row.productID,
          value: row.value
        })]
      : others;

    $ctrl.ngModel.$setViewValue(next);
  };
}

/* @ngInject */
function optionFuzzySearch() {
  /**
   * Filters a section's inventory options against its fuzzy search input.
   * Splitting on space lets the user enter multiple criteria — given combos
   * "Red - Large - Steak", "Blue - Large - Steak", "Red - Large - Bread",
   * the search "Red Large" matches "Red - Large - Steak" AND
   * "Red - Large - Bread". An empty input clears the filter so the full
   * list comes back.
   *
   * Search state lives on the section (not the controller) so each tracked
   * product gets its own independent search field — typing in the root's
   * search doesn't affect a sub-item's grid and vice versa.
   */
  return function (section) {
    if (!section.optionFuzzyInput) {
      section.filteredInventoryOptions = null;
      return;
    }

    const values = section.optionFuzzyInput.split(' ').filter(Boolean);

    section.filteredInventoryOptions = section.inventoryOptions.filter((io) =>
      values.every((value) =>
        io.name.toLowerCase().includes(value.toLowerCase())
      )
    );
  };
}

/* @ngInject */
function regenerate($ctrl) {
  /**
   * Top-level rebuild. Called from $onChanges and once at the end of
   * $onInit. Produces:
   *
   *   - $ctrl.productSections        per-product sections, each with its
   *                                  own optionRows + (if tracked)
   *                                  inventoryOptions
   *   - $ctrl.selectionListMasterIDs scope filter for inventory model writes
   *   - $ctrl.isReport               cached forReport result for buildSection
   *
   * Then runs the initial sync so that any non-inventory rows (text inputs,
   * dropdowns, multi-selects) push their seeded state into the model.
   * Inventory buttons are explicitly skipped from that sync — their state
   * is purely click-driven and is read back from the model on render.
   */
  return function () {
    if (!$ctrl.masterList || !$ctrl.product || !angular.isObject($ctrl.product)) {
      $ctrl.productSections = [];
      return;
    }

    $ctrl.isReport = !!$ctrl.forReport();

    const masterOptionLookup = {};

    /**
    *No filter for trackInventory + selection-list — that decision is now
    *made per-product in buildRow. All masters reach productMap and each
    *section decides whether to render dropdowns or inventory buttons
    *based on its own product's trackInventory.
    */
    const validOptionIDs = new Set(
      $ctrl.masterList.map((mo) => {
        masterOptionLookup[mo.id] = mo;
        return mo.id;
      })
    );

    /**
    *Capture the set of selection-list master IDs so toggleInventory and
    *isInventorySelected can scope themselves to just that slice of the
    *model and leave text-input entries on the same product alone.
    */
    $ctrl.selectionListMasterIDs = $ctrl.collectSelectionListMasterIDs(
      $ctrl.product,
      masterOptionLookup,
      new Set()
    );

    const productMap = $ctrl.buildProductMap({
      rootProduct: $ctrl.product,
      isReport: $ctrl.isReport,
      masterOptionLookup,
      validOptionIDs,
      modelValue: $ctrl.ngModel.$modelValue || [] 
    });

    /**
    *showSubLabels: only render sub-item product names when there's a real
    *ambiguity to disambiguate (i.e. multiple products in play).
    */
    const productIDList = Object.keys(productMap);
    const productHasOptions = productIDList.includes($ctrl.product.id);
    const showSubLabels = !productHasOptions || productIDList.length > 1;

    /**
    *Build a section per product. Each section knows whether its own
    *product tracks inventory and carries its own inventoryOptions list
    *(possibly empty). No top-level inventory state on the controller.
    */
    $ctrl.productSections = productIDList.map((key) =>
      $ctrl.buildSection(productMap[key], showSubLabels)
    );
  };
}

/* @ngInject */
function toLedgerItemOption() {
  /**
   * Shapes one combo option into a LedgerItemOption-compatible plain object.
   * Used by toggleInventory to convert the click target's options into
   * model entries. The shape matches what addSelectedOptions on the parent
   * expects (itemID, itemOptionID, optionMasterID, optionMasterName,
   * optionValue, priceChange, qtyChange).
   */
  return function (opt, productID) {
    return {
      itemID: productID,
      itemOptionID: opt.itemOptionID,
      optionMasterID: opt.masterOptionID,
      optionMasterName: opt.masterOptionName,
      optionValue: opt.itemOptionValue,
      priceChange: opt.priceChange,
      qtyChange: opt.qtyChange
    };
  };
}

/* @ngInject */
function toggleInventory($ctrl) {
  /**
   * Toggles the clicked combination's selection-list entries in ngModel
   * for the specified product.
   *
   * The model is shared with all other rows in the tree (text inputs on
   * the same product, dropdowns/text/buttons on other products), so we
   * scope the swap to just THIS product's selection-list slice. The
   * filter below keeps any entry that is NOT (this product's selection-
   * list slice). What that filter discards then either gets replaced
   * (when toggling onto a new combo) or simply left out (when toggling
   * the currently-selected combo off).
   *
   * Net effect: single-select per product among the inventory buttons,
   * with text-input rows on the same product AND every other product's
   * entries unaffected.
   */
  return function (option, productID) {
    const current = $ctrl.ngModel.$viewValue || [];

    // Keep everything that isn't a selection-list entry for this product.
    const others = current.filter(
      (m) =>
        !(
          m.itemID === productID &&
          $ctrl.selectionListMasterIDs.has(m.optionMasterID)
        )
    );

    const next = $ctrl.isInventorySelected(option, productID)
      ? others
      : [
        ...others,
        ...option.options.map((o) => $ctrl.toLedgerItemOption(o, productID))
      ];

    $ctrl.ngModel.$setViewValue(next);
  };
}

/* @ngInject */
function updateModel($ctrl) {
  /**
   * Idempotent add/remove on ngModel.$viewValue. Lazily initializes the
   * model array if it hasn't been set yet (parents commonly leave it
   * undefined until first interaction). Adds only if not present, removes
   * only if present — repeated calls with the same arguments are no-ops
   * after the first effective one.
   */
  return function (lio, remove) {
    const idx = $ctrl.ngModel.$viewValue.indexOf(lio);

    if (remove && idx > -1) $ctrl.ngModel.$viewValue.splice(idx, 1);
    if (!remove && idx === -1) $ctrl.ngModel.$viewValue.push(lio);
  };
}